import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Ticket, XIcon } from "lucide-react";
import CustomPaginationComponent from "@/components/common/custom-pagination-component";
import useCoupons from "@/features/coupons/hooks/use-coupons";
import { CouponCard } from "@/features/coupons/ui/coupon-card";
import VaulDrawer from "@/components/ui/drawer";
import AddEditCouponForm from "@/features/coupons/ui/coupon-form";
import { CouponData } from "@/features/coupons/schema/coupon-schema";
import StatusFilter from "@/features/coupons/ui/status-filter";
import TypeFilter from "@/features/coupons/ui/type-filter";
import { SearchBar } from "@/components/common/search-bar";
import { useHasAccess } from "@/components/common/has-access";
import { ContentLayout } from "@/components/common/content-layout";

export default function Coupons() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [showCouponDrawer, setShowCouponDrawer] = useState(false);
  // The drawer is how a coupon is VIEWED, not only edited, so it must open for
  // everyone who can see the list. Only what it lets you do depends on Edit.
  const canWrite = useHasAccess("write");
  const [selectedCoupon, setSelectedCoupon] = useState<CouponData>();
  // Single source of truth for what the drawer does. Never infer the mode from
  // selectedCoupon's truthiness — a duplicate prefill is a truthy object with
  // id: undefined and must NOT open as an edit.
  const [formMode, setFormMode] = useState<"create" | "edit" | "duplicate">("create");
  const navigate = useNavigate();

  // Get filter values from URL params
  const page = parseInt(searchParams.get("page") || "0", 10);
  const search = searchParams.get("search") || "";
  const status = searchParams.get("status") || undefined;
  const type = searchParams.get("type") || undefined;

  // Fetch coupons with filters
  const { data, error, isFetching, isLoading } = useCoupons(
    page,
    search,
    status,
    type
  );

  const coupons = data?.coupons || [];
  const currentPage = data?.currentPage || 0;
  const lastPage = data ? data.lastPage + 1 : 0;

  // Sanitize a coupon into a "new coupon" prefill and open the drawer in
  // duplicate mode. Shared by the card's Duplicate button and the one inside
  // the edit form, so the copy rules live in exactly one place.
  const handleDuplicateCoupon = (coupon: CouponData) => {
    setSelectedCoupon({
      ...coupon,
      id: undefined, // no id -> the form's submit guard routes to POST /coupon
      // The name IS the redemption code: applyCoupon matches on it and the
      // create DTO uppercases it, so the suffix must stay a typeable code —
      // "-COPY", never " (Copy)" (a space/parens would ship as a live code
      // shaped like "SAVE10 (COPY)" the moment the duplicate is activated).
      name: `${coupon.name}-COPY`,
      // ACTIVE bundles only. Create (unlike update) rejects retired bundles
      // with a 409, and a retired bundle renders no chip in the form (the
      // picker resolves ids against /bundle/active) — it would sit invisibly
      // in bundle_ids with no × to remove it, failing every save.
      bundles: coupon.bundles?.filter((bundle) => bundle.status === "ACTIVE"),
      // MUST be the uppercase enum value — zod requires "ACTIVE" | "INACTIVE",
      // and the form renders no status field. A duplicate is a brand-new
      // coupon and is created ACTIVE, matching a normal create: its name gets
      // a "-COPY" suffix so it never collides with the original's ACTIVE name,
      // and the care team edits the copied validity dates before saving.
      status: "ACTIVE",
      created_at: undefined,
      updated_at: undefined,
      usage_count: undefined, // fresh coupon starts at 0 (server default)
    } as unknown as CouponData);
    setFormMode("duplicate");
    setShowCouponDrawer(true);
  };

  const handlePageChange = (page: number) => {
    if (page >= 0 && page < lastPage) {
      // Keep the existing search params and just update the page
      const newParams = new URLSearchParams(searchParams);
      newParams.set("page", page.toString());

      const newUrl = `?${newParams.toString()}`;
      navigate(newUrl, { replace: true });
    }
  };

  return (
    <ContentLayout
      header={
        <div className="flex flex-wrap items-center gap-4">
          <StatusFilter />
          <TypeFilter />
          <div className="flex-1 min-w-[200px]">
            <SearchBar
              placeholder="Search Coupons..."
              urlParamStrategy="prefix"
              clearButton={{
                enabled: true,
                position: "inside",
                ariaLabel: "Clear search",
                icon: <XIcon className="w-4 h-4" />
              }}
              // fieldSelector={{
              //   enabled: true,
              //   fields: [
              //     { value: "name", label: "Name" },
              //     { value: "email", label: "Email" },
              //     { value: "phone", label: "Phone" }
              //   ],
              //   allowAll: true
              // }}
            />
          </div>
        </div>
      }
      data={coupons}
      isLoading={isLoading}
      isFetching={isFetching}
      error={error}
      errorMessage="Failed to load coupons"
      emptyState={{
        message: "No coupons found matching your criteria",
        icon: <Ticket className="h-8 w-8 text-muted-foreground" />
      }}
    >
      <CouponCard
        coupons={coupons}
        setSelectedCoupon={setSelectedCoupon}
        setShowCouponDrawer={setShowCouponDrawer}
        setFormMode={setFormMode}
        onDuplicate={handleDuplicateCoupon}
      />
      {coupons.length > 0 && (
        <CustomPaginationComponent
          currentPage={currentPage}
          lastPage={lastPage}
          onPageChange={handlePageChange}
        />
      )}
      <VaulDrawer
        drawerDirection="right"
        isOpen={showCouponDrawer}
        onOpenChange={setShowCouponDrawer}
        triggerChildren={null}
        contentChildren={
          <AddEditCouponForm
            // Remount on mode/coupon change: react-hook-form captures
            // defaultValues at mount, so duplicating from an OPEN edit form
            // must recreate the form to pick up the sanitized copy.
            key={`${formMode}-${selectedCoupon?.id ?? "new"}`}
            readOnly={!canWrite}
            editMode={formMode === "edit"}
            duplicateMode={formMode === "duplicate"}
            onClose={() => setShowCouponDrawer(false)}
            initialData={selectedCoupon}
            setShowCouponDrawer={setShowCouponDrawer}
            onDuplicate={() =>
              selectedCoupon?.id && handleDuplicateCoupon(selectedCoupon)
            }
          />
        }
      />
    </ContentLayout>

  );
}
